import * as React from 'react'
import { Save, Trash2, Clock, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePersistStore, type SavedSnapshot } from '@/stores/persistStore'
import { cn } from '@/lib/utils'

type ModuleKey = 'sp80022' | 'sp80090b' | 'tunableBias' | 'gaussian'

interface SnapshotManagerProps {
  module: ModuleKey
  hasResult: boolean
  onLoadSnapshot?: (snapshot: SavedSnapshot) => void
  className?: string
}

export function SnapshotManager({
  module,
  hasResult,
  onLoadSnapshot,
  className,
}: SnapshotManagerProps) {
  const [showSave, setShowSave] = React.useState(false)
  const [showList, setShowList] = React.useState(false)
  const [name, setName] = React.useState('')
  const { saveSnapshot, deleteSnapshot, getSnapshots } = usePersistStore()

  const snapshots = getSnapshots(module)

  const handleSave = () => {
    const label = name.trim() || `Run ${snapshots.length + 1}`
    saveSnapshot(module, label)
    setName('')
    setShowSave(false)
  }

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      {/* Save button */}
      {hasResult && (
        showSave ? (
          <div className="flex items-center gap-1">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Snapshot name..."
              className="h-7 w-36 text-xs"
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              autoFocus
            />
            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={handleSave}>
              <Save className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setShowSave(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setShowSave(true)}
          >
            <Save className="h-3 w-3" />
            Save Snapshot
          </Button>
        )
      )}

      {/* Saved snapshots dropdown */}
      {snapshots.length > 0 && (
        <div className="relative">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setShowList(!showList)}
          >
            <Clock className="h-3 w-3" />
            {snapshots.length} Saved
            <ChevronDown className={cn('h-3 w-3 transition-transform', showList && 'rotate-180')} />
          </Button>

          {showList && (
            <div className="absolute right-0 z-50 mt-1 w-64 rounded-md border bg-popover p-1 shadow-md">
              {snapshots.map((snap) => (
                <div
                  key={snap.id}
                  className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
                >
                  <button
                    type="button"
                    className="flex-1 text-left truncate"
                    onClick={() => {
                      onLoadSnapshot?.(snap)
                      setShowList(false)
                    }}
                  >
                    <span className="font-medium">{snap.name}</span>
                    <span className="ml-2 text-muted-foreground">
                      {snap.fileName} ({snap.sampleCount.toLocaleString()})
                    </span>
                  </button>
                  <button
                    type="button"
                    className="p-0.5 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteSnapshot(snap.id)
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
